import { useEffect } from "react";
import { SITE_NAME, OG_IMAGE, canonical, isSnapshot, isProductionHost } from "@/lib/seo";

/**
 * Route-level SEO. All head tags are managed imperatively inside an effect and
 * the component renders nothing. This mirrors how react-helmet works with
 * react-snap: React never hydrates <head> metadata (avoiding hydration
 * mismatch / React #418), while react-snap still serialises the post-effect
 * <head> so crawlers get full title/description/canonical/OG/JSON-LD.
 */
export default function Seo({
  title,
  description,
  path = "/",
  image = OG_IMAGE,
  type = "website",
  noindex = false,
  jsonLd = [],
  alternates = null,
  locale = "en",
  htmlLang = null,
  htmlDir = null,
}) {
  const url = canonical(path);
  const schemas = Array.isArray(jsonLd) ? jsonLd.filter(Boolean) : [jsonLd].filter(Boolean);

  useEffect(() => {
    let robots;
    if (noindex) robots = "noindex,nofollow,noarchive";
    else if (!isSnapshot() && !isProductionHost()) robots = "noindex,nofollow";
    else robots = "index,follow,max-image-preview:large";

    // Clear any tags this component previously wrote (or that react-snap baked).
    document.head.querySelectorAll("[data-seo]").forEach((e) => e.remove());

    const add = (tag, attrs, text) => {
      const el = document.createElement(tag);
      el.setAttribute("data-seo", "");
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      if (text != null) el.textContent = text;
      document.head.appendChild(el);
    };

    document.title = title;
    add("meta", { name: "description", content: description });
    add("meta", { name: "robots", content: robots });
    add("link", { rel: "canonical", href: url });

    if (Array.isArray(alternates)) {
      alternates.forEach((a) => add("link", { rel: "alternate", hreflang: a.hreflang, href: a.href }));
    }

    add("meta", { property: "og:title", content: title });
    add("meta", { property: "og:description", content: description });
    add("meta", { property: "og:url", content: url });
    add("meta", { property: "og:type", content: type });
    add("meta", { property: "og:site_name", content: SITE_NAME });
    add("meta", { property: "og:image", content: image });
    add("meta", { property: "og:image:width", content: "1200" });
    add("meta", { property: "og:image:height", content: "630" });
    add("meta", { property: "og:locale", content: locale });

    add("meta", { name: "twitter:card", content: "summary_large_image" });
    add("meta", { name: "twitter:title", content: title });
    add("meta", { name: "twitter:description", content: description });
    add("meta", { name: "twitter:image", content: image });

    schemas.forEach((data) => add("script", { type: "application/ld+json" }, JSON.stringify(data)));

    // Per-page <html> lang/dir (used by the Arabic legal page). Restored on unmount.
    let prevLang, prevDir;
    if (htmlLang || htmlDir) {
      const html = document.documentElement;
      prevLang = html.getAttribute("lang");
      prevDir = html.getAttribute("dir");
      if (htmlLang) html.setAttribute("lang", htmlLang);
      if (htmlDir) html.setAttribute("dir", htmlDir);
    }
    return () => {
      if (htmlLang || htmlDir) {
        const html = document.documentElement;
        html.setAttribute("lang", prevLang || "en");
        if (prevDir) html.setAttribute("dir", prevDir); else html.removeAttribute("dir");
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, title, description, noindex]);

  return null;
}
